import { type ReactNode, useState } from "react";
import { Button, InputLabel, Stack } from "@mui/material";

import { RichTreeView } from "@mui/x-tree-view";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ZoneBreadcrumbs } from "components/ZoneBreadcrumbs/ZoneBreadcrumbs";
import { IZone } from "interfaces/zone.interface";
import { makeZoneTree } from "utils/zone.util";

function reduceZoneSelection(zoneIds: string[], zones: IZone[]) {
  return zoneIds
    .map((id) => zones.find((z) => z.id === id))
    .filter((zone) => !!zone)
    .filter(
      (zone) =>
        !zoneIds.some(
          (id) => zone.path.includes(id) && !zone.path.endsWith(id),
        ),
    )
    .map((zone) => zone.id);
}

type ZoneInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  control: Control<T>;
  zones: IZone[];
};

export function ZoneInput<T extends FieldValues>({
  fieldName,
  label,
  control,
  zones,
}: ZoneInputProps<T>) {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(zones.map((zone) => zone.id));

  const [toggleZoneTree, setToggleZoneTree] = useState(false);

  const {
    field: { ...field },
  } = useController({
    name: fieldName,
    control,
  });

  return (
    <>
      <InputLabel>{label}</InputLabel>
      {toggleZoneTree ? (
        <RichTreeView
          items={makeZoneTree(zones)}
          getItemId={(z) => z.id}
          getItemLabel={(z) => z.name}
          expandedItems={expanded}
          onExpandedItemsChange={(_, expandedIds) => setExpanded(expandedIds)}
          multiSelect
          checkboxSelection
          isItemDisabled={(item) =>
            field.value.some(
              (value: string) =>
                item.path.includes(value) && !item.path.endsWith(value),
            )
          }
          selectedItems={field.value || []}
          onSelectedItemsChange={(_, selectedZoneIds) => {
            field.onChange(reduceZoneSelection(selectedZoneIds, zones));
          }}
        />
      ) : (
        <Stack gap={1}>
          {zones
            .filter((z) => field.value.includes(z.id))
            .map((zone) => (
              <ZoneBreadcrumbs
                key={zone.id}
                path={zone.path
                  .split(".")
                  .map((id) => zones.find((z) => z.id === id))
                  .filter((zone) => !!zone)
                  .map((zone) => zone.name)}
              />
            ))}
        </Stack>
      )}
      <Button
        variant={"text"}
        onClick={() => setToggleZoneTree(!toggleZoneTree)}
      >
        {toggleZoneTree ? t("close_zone_tree") : t("change_zones")}
      </Button>
    </>
  );
}
